<template>
  <base-hm-input :id="id"
                 :class="[
                   $attrs['class'],
                   { 'has-inline-icon-left': hasSlot('icon-inline-left') },
                   { 'has-inline-icon-right': hasSlot('icon-inline-right') },
                   { 'component__hm-select--visible': selectVisible },
                   { 'component__hm-select--autocomplete': hideActionButtons }
                 ]"
                 :style="iconCountStyleVariables"
                 :label="label"
                 :help="help"
                 :has-focus="hasFocus"
                 :hover="hover"
                 :is-empty="isValueEmpty"
                 :name="name"
                 :cols="cols"
                 :hidden="hidden"
                 :disabled="disabledCombined">
    <div v-if="!inputLoaded" class="dropdown v-select hm-input__select hm-input__select--vselect single searchable"></div>
    <v-select v-if="inputLoaded"
              ref="autocomplete"
              :id="`${id}-input`"
              :validate="`${validate}`"
              :value="childSelectValue"
              :data-vv-as="label"
              :data-vv-name="name"
              :disabled="disabledCombined"
              :clearable="clearable"
              :options="selectItems"
              :taggable="taggable"
              :get-option-label="getOptionLabel"
              :select-on-tab="true"
              :create-option="createOption"
              :allow-duplicate-tags="taggableExistingValue"
              :label="labelKey"
              :class="['hm-input__select', 'hm-input__select--vselect', { 'privacy--blur': privacyBlur }]"
              type="text"
              @input="onInput"
              @option:created="onCreateOption($event)"
              @search="onSearch($event)"
              @search:focus="onFocus"
              @search:blur="onMyBlur">
      <template v-if="hasSlot('option')" slot="option" slot-scope="option">
        <slot :option="option" :labelKey="labelKey" name="option" />
      </template>
      <span slot="spinner" slot-scope="mutableLoading">
        <div class="spinner">{{ $t('inputs.hmSelect.loading') }} {{ mutableLoading }}</div>
      </span>
      <span slot="no-options">{{ $t('inputs.hmSelect.noResults') }}</span>
    </v-select>
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper  hm-input__icon__wrapper--left" @click="$event.stopPropagation();">
      <slot :hover="hover" :focus="hasFocus" :value="childSelectValue" name="icon-inline-left"></slot>
    </div>
    <!-- buttons outside input - on the right -->
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper  hm-input__icon__wrapper--right" @click="$event.stopPropagation();">
      <slot :hover="hover" :focus="hasFocus" :value="childSelectValue" name="icon-inline-right"></slot>
    </div>
  </base-hm-input>
</template>

<script>

import { isNil, isArray, isObject, template, isString, isEmpty, find, debounce, isFunction, includes } from 'lodash';
import HmInputMixin from '@/mixins/HmInputMixin';
import uuid from 'uuid';
import LocaleMixin from '@/mixins/LocaleMixin';

export default {
  name: 'HmSelect',

  mixins: [HmInputMixin, LocaleMixin],

  props: {
    value: {
      required: true,
    },
    valueExpression: {
      type: String,
      default: 'id',
    },
    autocompleteUrl: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    labelExpression: {
      // type: String,
      // eslint-disable-next-line no-template-curly-in-string
      default: '${option.name}',
    },
    options: {
      default: null,
    },
    taggable: {
      required: false,
      type: Boolean,
      default: false,
    },
    taggableExistingValue: {
      required: false,
      type: Boolean,
      default: false,
    },
    clearable: {
      required: false,
      type: Boolean,
      default: true,
    },
    index: {
      required: false,
      type: String,
      default: null,
    },
    createOption: {
      type: Function,
      default(newOption) {
        if (typeof this.mutableOptions[0] === 'object') {
          newOption = { name: newOption };
        }
        this.$emit('option:created', newOption);
        return newOption;
      },
    },
  },

  data: function () {
    return {
      lastSearch: null,
      hover: false,
      loaded: false,
      selectItems: [],
      childSelectValue: null,
      debouncedUpdateSelectItems: null,
      autocompleteKey: null,
      labelKey: 'name',
    };
  },

  computed: {
    hideActionButtons: function () {
      return !isEmpty(this.autocompleteUrl);
    },
    selectVisible: function () {
      return this.hasFocus;
    },
    iconCountLeft: function () {
      const slot = this.getSlot('icon-inline-left');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountRight: function () {
      const slot = this.getSlot('icon-inline-right');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountStyleVariables: function () {
      return {
        '--icons-left': this.iconCountLeft,
        '--icons-right': this.iconCountRight,
      };
    },
  },


  watch: {
    childSelectValue: function (newVal, oldVal) {
      if (isObject(newVal)) {
        // eslint-disable-next-line no-prototype-builtins
        if (newVal.hasOwnProperty(this.valueExpression)) {
          this.internalValue = newVal[this.valueExpression];
          return;
        } else if (!isEmpty(this.autocompleteUrl) && this.taggable) {
          this.internalValue = null;
          return;
        }
      }

      this.internalValue = newVal;
    },
    internalValue: function (newVal, oldVal) {
      const $vm = this;
      if (!isNil(newVal) && newVal !== oldVal && isEmpty(this.autocompleteUrl)) {
        if (this.taggable === true) {
          let tmp = this.selectItems.find(it => it[$vm.valueExpression] === $vm.internalValue);
          if (isNil(tmp)) {
            if (isString(newVal)) tmp = { [$vm.valueExpression]: newVal, name: newVal };
            else tmp = { [$vm.valueExpression]: newVal.name || newVal.id };
          }
          if (this.selectItems.filter(it => it[this.valueExpression] === tmp[this.valueExpression]).length === 0) {
            this.selectItems.push(tmp);
          }
          this.childSelectValue = tmp;
        } else {
          this.childSelectValue = this.selectItems.find(it => it[$vm.valueExpression] === $vm.internalValue);
        }
      } else if (!isNil(newVal) && newVal !== oldVal && !isEmpty(this.autocompleteUrl)) {
        this.updateValue(newVal);
      } else if (isNil(newVal) && newVal !== oldVal && !isEmpty(this.autocompleteUrl)) {
        this.selectItems = [];
        this.childSelectValue = null;
        this.updateSelectItems();
      }
    },
    value: function (newVal) {
      if (isNil(newVal)) {
        this.selectItems = [];
        this.childSelectValue = null;
        this.internalValue = null;
        this.updateSelectItems();
      }
    },
    options: function () {
      this.updateSelectItems();
    },
  },

  created: function () {
    const $vm = this;
    this.inputLoaded = false;

    if (this.autocompleteUrl && isString(this.value) && !isEmpty(this.value)) {
      const queryString = this.value;
      const key = uuid.v4();
      this.autocompleteKey = key;

      this.axios.get(this.autocompleteUrl, {
        params: {
          q: queryString,
          type: 'autocomplete',
        },
      })
        .then((response) => {
          if (key === this.autocompleteKey) {
            if (isObject(response.data.data) && !isArray(response.data.data)) {
              $vm.childSelectValue = Object.entries(response.data.data)
                .map(([k, v]) => ({
                  ...v,
                  id: k,
                }))[0];
            } else if (isArray(response.data.data)) {
              $vm.childSelectValue = response.data.data[0];
            }
            $vm.$nextTick(() => {
              $vm.inputLoaded = true;
            });
          }
        });
    } else {
      this.updateSelectItems();
      this.inputLoaded = true;
    }

    if (!isNil(this.internalValue)) {
      this.childSelectValue = this.selectItems.find(it => it[$vm.valueExpression] === $vm.internalValue);
    }

    if (!isNil(this.internalValue) && isNil(this.childSelectValue) && this.taggable) {
      this.childSelectValue = this.internalValue;
    }

    this.debouncedUpdateSelectItems = debounce(this.updateSelectItems, 250);
  },


  methods: {
    hasSlot: function (slotName) {
      return !isNil(this.$slots[slotName]) || !isNil(this.$scopedSlots[slotName]);
    },
    getSlot: function (slotName) {
      return !isNil(this.$slots[slotName]) ? this.$slots[slotName] : this.$scopedSlots[slotName];
    },
    isNil: function (arg) {
      return isNil(arg);
    },
    isEmptyString: function (arg) {
      return isEmpty(arg);
    },
    updateSelectItems: function (event) {
      const $vm = this;
      const queryString = event;
      if (this.autocompleteUrl) {
        if (isString(queryString) && !isEmpty(queryString)) {
          this.axios.get(this.autocompleteUrl, {
            params: {
              q: queryString,
              type: 'autocomplete',
            },
          })
            .then((response) => {
              if (isObject(response.data.data) && !isArray(response.data.data)) {
                $vm.selectItems = Object.entries(response.data.data)
                  .map(([k, v]) => ({
                    ...v,
                    id: k,
                    [this.labelKey]: v.name,
                  }));
              } else if (isArray(response.data.data)) {
                $vm.selectItems = response.data.data;
              } else {
                $vm.selectItems = [];
              }
            });
        } else {
          this.selectItems = [];
        }
      } else if (isObject(this.options) && !isArray(this.options)) {
        this.selectItems = Object.entries(this.options)
          .map(([k, v]) => {
            if (isString(v)) {
              return {
                name: v,
                id: k,
              };
            } else {
              return {
                ...v,
                id: v.id || k,
              };
            }
          });
      } else if (isArray(this.options)) {
        this.selectItems = this.options;
      }
    },
    getOptionLabel: function (option) {
      if (isObject(option)) {
        const args = {
          option: option,
          $t: this.$t,
          _i18n: this._i18n,
          $shortLocale: this.$shortLocale,
          toDate: value => (isNil(value) ? '' : new Intl.DateTimeFormat(
            window.i18n.locale,
            {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
            },
          ).format(this.$moment(value, 'YYYYMMDD').toDate())),
          toShortDate: value => (isNil(value) ? '' : new Intl.DateTimeFormat(
            window.i18n.locale,
            {
              day: '2-digit',
              month: '2-digit',
            },
          ).format(this.$moment(value, 'YYYYMMDD').toDate())),
        };

        if (isFunction(this.labelExpression)) {
          return this.labelExpression(args);
        } else {
          return template(this.labelExpression)(args);
        }
      } else if (this.autocompleteUrl) {
        return option;
      } else {
        return option;
      }
    },
    onCreateOption: function (newOption) {
      if (this.taggable === true) {
        let tmp = {};
        if (isString(newOption)) {
          tmp = { [this.valueExpression]: newOption };
        } else {
          tmp = { [this.valueExpression]: newOption.name || newOption.id };
        }

        if (!isEmpty(this.index)) tmp[this.index] = tmp[this.valueExpression];
        if (!isEmpty(this.labelKey)) tmp[this.labelKey] = tmp[this.valueExpression];

        this.$emit('option:created', tmp);

        if (isEmpty(this.autocompleteUrl)) {
          if (this.selectItems.filter(it => it[this.valueExpression] === tmp[this.valueExpression]).length === 0) {
            this.selectItems.push(tmp);
          }
          this.childSelectValue = tmp;
        }
      }
      this.lastSearch = null;
    },
    onFocus: function () {
      if (this.taggable === true && isEmpty(this.autocompleteUrl)) {
        // this.childSelectValue = null;
      }
      this.hasFocus = true;
      this.$emit('focus');
    },
    updateValue: function (queryString = null, force = false) {
      if (isEmpty(queryString)) queryString = this.internalValue;

      if (force === false && !isNil(this.childSelectValue) && queryString === this.childSelectValue.id) {
        return;
      }

      this.axios.get(this.autocompleteUrl, {
        params: {
          q: queryString,
          type: 'autocomplete',
        },
      })
        .then((response) => {
          if (isObject(response.data.data) && !isArray(response.data.data)) {
            this.childSelectValue = Object.entries(response.data.data)
              .map(([k, v]) => ({
                ...v,
                id: k,
              }))[0];
          } else if (isArray(response.data.data)) {
            this.childSelectValue = response.data.data[0];
          }
        });
    },
    onSearch($event) {
      const search = isEmpty($event) ? null : $event;
      if (this.hasFocus) {
        this.debouncedUpdateSelectItems(search);
        this.lastSearch = search;
      }
    },
    onMyBlur($event) {
      this.onBlur($event);
      if (isEmpty(this.autocompleteUrl) && this.taggable && !isEmpty(this.lastSearch)) {
        this.onCreateOption({ name: this.lastSearch });
      }
      this.lastSearch = null;
    },
    onInput(newVal) {
      this.$emit('select', newVal);

      if (isObject(newVal)) {
        // eslint-disable-next-line no-prototype-builtins
        if (newVal.hasOwnProperty(this.valueExpression)) {
          this.internalValue = newVal[this.valueExpression];
          this.lastSearch = null;
          return;
        } else if (isEmpty(this.autocompleteUrl) && this.taggable) {
          this.internalValue = newVal.name || newVal.id || null;
          this.lastSearch = null;
          return;
        } else if (!isEmpty(this.autocompleteUrl) && this.taggable) {
          this.internalValue = null;
          return;
        }
      }

      this.internalValue = newVal;
      this.lastSearch = null;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
</style>

<style lang="scss">
  @import '~styles/import';

  #hm--vue {
    .component__hm-select {

      .v-select.disabled .dropdown-toggle, .v-select.disabled .dropdown-toggle .clear, .v-select.disabled .dropdown-toggle input, .v-select.disabled .open-indicator, .v-select.disabled .selected-tag .close {
        background-color: $lightgray;
        height: 3.0rem;
        border-radius: 0.3rem;
        pointer-events: none;
      }

      .hm-input--disabled {
        .inline-button {
          background-color: $white;
          border: 1px solid $middlegray;

          &[disabled] {
            background-color: $lightgray;
          }

          &:last-child {
            border-top-right-radius: 0.3rem;
            border-bottom-right-radius: 0.3rem;
          }
        }
      }

      .inline-button {
        @include icon-color($middlegray);
        @include icon-hover($text);
        &:hover {
          cursor: pointer;
        }
      }

      &.has-inline-icon-left {
        .vs__selected-options {
          padding-left: 0;
        }
      }

      .dropdown-toggle {
        border: none !important;
        padding-left: calc(var(--icons-left) * 3.2rem);
        padding-right: calc(var(--icons-right) * 3.2rem);
      }

      .v-select .dropdown-menu {
        padding-top: 1px;
        & > .highlight > a {
          background: $lightmint;
          color: $text;
        }
        & > .active > a {
          background: none;
          color: $text;
        }
        a {
          padding: 4px 12px;
        }

        li:not(:last-of-type) {
          border-bottom: 1px solid $lightmiddlegray;
          &.highlight {
            border-bottom: 1px solid $middlemint;
            position: relative;
            &:before {
              content: '';
              position: absolute;
              top: -1px;
              left: 0;
              width: 100%;
              height: 1px;
              display: block;
              background-color: $middlemint;
            }
          }
        }
        .gravatar__wrapper img {
          border: 1px solid $middlemint;
        }
      }

      .v-select {
        &.open {
          &.single {
            &.searching {
              .selected-tag {
                opacity: 0 !important;
              }
            }
          }
          .open-indicator {
            &:before {
              background-image: url(~svg/collapse_active.svg)!important;
            }
          }
        }
      }

      input[type=search] {
        padding: 0;
      }

      &--underneath {
        position: absolute;
        top: -0.2rem; // adjustment for the two borders
        z-index: 0;

        .hm-input__select {
          border: none;
        }

        .hm-input {
          &__label {
            display: none;
          }
          &__helper-text {
            display: none;
          }
          &__icon__wrapper {
            display: none;
          }
        }
      }

      .vs__selected-options {
        max-width: calc(100% - 2 * 3.2rem);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-left: 1rem;

        * {
          height: 3.2rem;
          padding-top: 0.9rem;
          padding-bottom: 0.5rem;
          margin: 0;
          display: block;
          max-width: 100%;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          line-height: 1.6rem;
          border: none;
          padding-left: 0;
          padding-right: 0;

          &:focus {
            height: 3.2rem !important;
            padding-top: 0.9rem !important;
            padding-bottom: 0.5rem !important;
            margin: 0 !important;
            display: block !important;
            max-width: 100% !important;
            overflow: hidden !important;
            white-space: nowrap !important;
            text-overflow: ellipsis !important;
            line-height: 1.6rem !important;
            border: none !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
        }
      }

      .vs__actions {
        padding: 0;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-areas: 'clear open';
        width: 2 * 3.2rem;
        min-width: 2 * 3.2rem;
        max-width: 2 * 3.2rem;
        height: 3.2rem;

        .clear {
          width: 3.2rem;
          height: 3.2rem;
          padding: 0;
          margin: 0;
          grid-area: 'clear';

          span {
            width: 3.2rem;
            height: 3.2rem;
            display: block;
            content: '';
            color: transparent;
            background-image: url(~svg/delete.svg);
            background-position: center center;
            background-repeat: no-repeat;
            position: relative;

            &:hover {
              background-image: url(~svg/delete_hover.svg);
            }
          }
        }

        .open-indicator {
          width: 3.2rem;
          height: 3.1rem;
          grid-area: open;
          overflow: hidden;
          &:before {
            width: 3.2rem;
            height: 3.2rem;
            transform: none !important;
            content: ' ';
            border: none;
            background-image: url(~svg/collapse.svg);
            background-position: center center;
            background-repeat: no-repeat;
            border-left: 1px solid $middlegray;
          }
        }
      }

      &--autocomplete {
        .vs__actions {
          display: none;
        }

        .vs__selected-options {
          max-width: calc(100%);
        }
      }

    }
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmSelect.vue